import type { Icon } from "@tabler/icons-react";
import {
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { useT } from "../i18n/provider";
import { cx, ICON_STROKE } from "../lib/cx";

/**
 * The components this console decided — docs/24 §11 and §12.
 *
 * `src/components/ui/` is vendored shadcn and stays close to upstream; anything
 * this console chose lives here or in the token layer.
 */

/**
 * §6.5 / §11.2: the tooltip trigger is a `<span>`, not the button.
 *
 * A refused action is a disabled button, `disabled:pointer-events-none` means it
 * never sees a hover, and a tooltip that cannot open takes the refusal reason
 * with it — «this payout is held pending KYB re-verification» would be a
 * greyed-out glyph and nothing else. So the span wraps the control and carries
 * the hover.
 *
 * `title` is deliberately absent: the browser would stack a second, late tip
 * under this one.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span
      className="relative inline-flex"
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={() => setOpen(false)}
    >
      <span aria-describedby={open ? id : undefined} className="inline-flex">
        {children}
      </span>
      {open && (
        <span
          id={id}
          role="tooltip"
          /* §3: ToolTip takes 4px rather than 8px "because of its small size" —
             the one documented exception. §9.4: acrylic on a transient surface. */
          className="acrylic elevated pointer-events-none absolute top-full left-1/2 z-70 mt-1 -translate-x-1/2 rounded-md border border-border px-2 py-1 text-xs whitespace-nowrap text-foreground shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Required, and it becomes both the `aria-label` and the tooltip. */
  label: string;
  icon: Icon;
  /** When set the button is refused, and THIS is what the tooltip says instead. */
  refusal?: string;
  tone?: "ghost" | "primary" | "critical";
}

/**
 * §11.2: a per-item verb is an icon button, never a typed glyph.
 *
 * Not `▲`, `▼`, `✕` in unstyled buttons at `text-xs`, faded to `opacity-20` when
 * refused. Four failure modes at once: a 12px triangle is a ~9px target against
 * the 40px §12 asks of a coarse pointer; at 20 % opacity a refused control is
 * below every contrast floor and reads as a rendering fault rather than as «not
 * now»; an unstyled button takes no hover and shows no focus, so the keyboard
 * cannot be seen; and a glyph with no accessible name is announced as «button».
 */
export function IconButton({ label, icon: Glyph, refusal, tone = "ghost", ...rest }: IconButtonProps) {
  const disabled = rest.disabled || refusal !== undefined;
  return (
    <Tooltip text={refusal ?? label}>
      <button
        type="button"
        {...rest}
        disabled={disabled}
        aria-label={label}
        className={cx(
          "touch-target inline-flex h-7 w-7 items-center justify-center rounded-md outline-none",
          tone === "ghost" && "text-muted-foreground hover:bg-control-hover hover:text-foreground",
          tone === "primary" && "control text-primary",
          tone === "critical" && "control text-critical",
          disabled && "cursor-not-allowed text-disabled-foreground hover:bg-transparent",
          rest.className,
        )}
      >
        <Glyph size={16} stroke={ICON_STROKE} aria-hidden />
      </button>
    </Tooltip>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary" | "critical";
  icon?: Icon;
  busy?: boolean;
}

/**
 * §11.4: a busy button says so — a spinner in the ICON slot, and **the label must
 * not change**. «Үүсгэх» → «Хадгалж байна…» rewrites the word and the width
 * mid-press, so the control moves out from under the pointer at the moment it is
 * working. This is worse in Mongolian, where the two strings differ in length by
 * more than they do in English.
 */
export function Button({ variant = "default", icon: Glyph, busy, children, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        "control inline-flex h-8 min-w-16 items-center justify-center gap-1.5 px-3 text-sm outline-none",
        // A disabled primary button drops the accent: the fill is what says
        // «this is the thing this page is for», and an accent-filled control
        // that does nothing is the loudest lie on the screen.
        variant === "primary" &&
          "border-primary bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active disabled:border-border disabled:bg-control-hover disabled:text-disabled-foreground",
        variant === "critical" && "text-critical",
        rest.className,
      )}
    >
      {busy ? <Spinner /> : Glyph ? <Glyph size={16} stroke={ICON_STROKE} aria-hidden /> : null}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/**
 * §11.3: a field's label is body-semibold; its hint is the caption.
 *
 * Not `text-xs text-muted-foreground` for BOTH — 12px is the size §4.2 calls the
 * floor for Mongolian Cyrillic, in the tone reserved for text of lesser
 * importance, and identical to its own hint. Failure mode: «Багтаамж» and
 * «Хоосон бол хязгааргүй» read as one two-line grey block, so the name of the
 * field and the note about it are typographically the same thing.
 *
 * What is marked is **optional**, not required, because most fields on these
 * forms are required and marking the majority is noise.
 */
export function Field({
  label,
  hint,
  error,
  optional,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) {
  const { i18n } = useT();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-foreground">
        {label}
        {optional && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">{i18n._("optional")}</span>
        )}
      </label>
      {children}
      {/* A hint does not belong in a ROW of fields: `items-end` aligns the
          bottoms, so a hint under one control lifts that control alone and four
          inputs land at four heights. Callers put a hinted field on its own row. */}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-critical">{error}</p>}
    </div>
  );
}

/**
 * A width the caller passes wins over the default.
 *
 * Tailwind resolves two utilities for the same property by their order in the
 * GENERATED stylesheet, not by their order in the class attribute — so a
 * `className="w-48"` next to a built-in `w-full` is a coin toss that this
 * console lost visibly: the dispatch board's node field ran to 420px and pushed
 * the export verbs onto a second toolbar band, which is exactly the «four bands
 * before a row of data» §6 exists to prevent. `tailwind-merge` is the general
 * answer and is not a dependency here; one explicit rule is enough for one
 * conflicting property.
 */
function sized(className: string | undefined, base: string): string {
  return cx(base, /(^|\s)(w-|min-w-|max-w-)/.test(className ?? "") ? "" : "w-full", className);
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={sized(props.className, "field h-8 px-2 text-sm outline-none")} />;
}

/**
 * §11.7: a `<select>` stays native until the list is one you SEARCH.
 *
 * Keep the platform control for a closed vocabulary — an order status, a
 * visibility, a tax category, a merchant tier. Three to six fixed options is a
 * list you read, and a search field over it is more control than the choice
 * deserves. A reference list (a category, a brand, a merchant, a node) takes the
 * combobox in `components/ui/combobox.tsx` instead.
 */
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={sized(props.className, "control h-8 px-2 text-sm outline-none")} />;
}

/**
 * §11.1: loading, empty, and empty-because-nothing-is-wrong are THREE states.
 *
 * `Loading` draws the SHAPE of what is coming — skeleton rows, `aria-busy`, and
 * the wait in `sr-only` text for the reader a skeleton says nothing to.
 *
 * The shape to avoid is `loading ? <Empty>Уншиж байна…</Empty> : rows.length ? …
 * : <Empty>Ноорог алга.</Empty>` — one grey line, centred, in the same place, for
 * «the answer has not arrived» and for «the answer is zero». The dashboard is
 * where it bites: that screen exists to answer «what is waiting for me?», and if
 * its cards say «nothing pending» while their fetches are open then for as long
 * as the network takes they state, in finished-looking words, that there is no
 * merchant waiting for approval. Somebody who reads that and closes the tab has
 * been told something untrue by a page that had not been told anything.
 */
export function Loading({ rows = 3, label }: { rows?: number; label?: string }) {
  const { i18n } = useT();
  return (
    <div aria-busy="true" className="flex flex-col gap-2">
      <span className="sr-only">{label ?? i18n._("Loading…")}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-4 animate-pulse rounded-sm bg-muted" style={{ width: `${88 - index * 14}%` }} />
      ))}
    </div>
  );
}

/**
 * `Empty` draws the ANSWER, and takes a title, an icon and an action. An empty
 * screen is an invitation: where a verb would fill it, the verb goes there
 * rather than being hunted for.
 *
 * `good` is the third state. **Zero is sometimes good news and should read that
 * way** — «Бүгд цэгцтэй» under a tick is what an empty approval queue means;
 * «Хүлээгдэж буй зүйл алга» is the same fact reported as an absence.
 */
export function Empty({
  title,
  description,
  icon: Glyph,
  good,
  action,
}: {
  title: string;
  description?: string;
  icon?: Icon;
  good?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      {Glyph && (
        <Glyph
          size={24}
          stroke={ICON_STROKE}
          aria-hidden
          className={good ? "text-success" : "text-muted-foreground"}
        />
      )}
      <p className={cx("text-sm font-semibold", good ? "text-success" : "text-foreground")}>{title}</p>
      {description && <p className="max-w-prose text-xs text-muted-foreground">{description}</p>}
      {action}
    </div>
  );
}

/**
 * §6.4: a grid is NEVER wrapped in a card. Cards are still right for a page of
 * forms and figures — and that is all this is for.
 */
export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "elevated rounded-xl border border-border bg-card p-4 text-card-foreground",
        className,
      )}
    >
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between gap-2">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}
